import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  SimpleChanges
} from '@angular/core';
import {Employment, EmploymentEvent, MasterEntities} from '../../master.entities.models';
import {compareById, compareYesNoBoolean} from '../../utils';
import {FormControl} from '@angular/forms';
import {Employee} from '../../list/employee-list.model';
import {AuthenticationService} from '../../../login/auth.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {SpinnerService} from '../../../spinner/spinner.service';
import {MasterEmployeeService} from '../../master.employee.service';
import {finalize} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {MatSelectChange} from '@angular/material/select';
import {DatePipe} from '@angular/common';

@Component({
  selector: 'app-employment',
  templateUrl: './employment.component.html',
  styleUrls: ['./employment.component.css']
})
export class EmploymentComponent implements OnInit, OnChanges {

  SERVICE_PERCENT_DYNAMIC_VALUE = '22-24';
  @Input() employment: Employment;
  @Input() employee: Employee;
  @Input() masterEntities: MasterEntities;
  @Output() employmentEventEmitter = new EventEmitter<EmploymentEvent>();
  hiredOn = new FormControl();
  effectiveOn = new FormControl();
  terminatedOn = new FormControl();
  benefitClassEffectiveDate = new FormControl();
  prosalEffectiveDate = new FormControl();
  prosalEndDate = new FormControl();
  payRateEffectiveDate = new FormControl();
  serviceExclusions = new FormControl();
  productExclusions = new FormControl();
  servicePercentErrorMessage: string;
  productPercentErrorMessage: string;
  prosalAnnualSalaryErrorMessage: string;

  constructor(public authService: AuthenticationService,
              private spinnerService: SpinnerService,
              private masterEmployeeService: MasterEmployeeService,
              private toastrService: ToastrService) {
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.employment) {
      this.hiredOn.setValue(this.employment.hiredOn);
      this.effectiveOn.setValue(this.employment.effectiveOn);
      this.terminatedOn.setValue(this.employment.terminatedOn);
      this.benefitClassEffectiveDate.setValue(this.employment.benefitClassEffectiveDate);
      this.prosalEffectiveDate.setValue(this.employment.prosalInfo.effectiveDate);
      this.prosalEndDate.setValue(this.employment.prosalInfo.endDate);
      this.payRateEffectiveDate.setValue(this.employment.payRate.effectiveDate);
      this.serviceExclusions.setValue(this.employment.excludedAaahaAccounts.filter(
        item => item.revenueCategory === 'SERVICE').map(aahaAccount => aahaAccount.id));
      this.productExclusions.setValue(this.employment.excludedAaahaAccounts.filter(
        item => item.revenueCategory === 'PRODUCT').map(aahaAccount => aahaAccount.id));
    }
  }

  public compareOption(item1, item2) {
    return compareById(item1, item2);
  }

  isNew() {
    return !this.employment?.id;
  }

  getEmploymentIndex() {
    return this.isNew() ? 'new' : this.employment.id;
  }

  public compareByBool(item1, item2): boolean {
    return compareYesNoBoolean(item1, item2);
  }

  onExcludeSalaryChange($event: MatCheckboxChange) {
    this.updateEmployment(this.masterEmployeeService.toggleExcludeSalary(this.employee.id, this.employment.id, $event.checked));
  }

  onProsalEffectiveDateChange($event) {
    const pipe = new DatePipe('en-US');
    const formattedDate = pipe.transform($event.value, 'yyyy-MM-dd','+0000' );
    this.updateEmployment(this.masterEmployeeService.changeProsalEffectiveDate(this.employee.id, this.employment.id, formattedDate));
  }

  onProsalAnnualSalaryChange($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    if (inputElement.validity.valid) {
      this.prosalAnnualSalaryErrorMessage = '';
      this.updateEmployment(
        this.masterEmployeeService
          .changeProsalAnnualSalary(this.employee.id, this.employment.id, this.employment.prosalInfo.annualSalary));
    } else {
      this.prosalAnnualSalaryErrorMessage = 'Value entered is invalid. Please use format ddd.dd';
    }
  }

  clearProsalEffectiveDate() {
    this.prosalEffectiveDate.setValue(null);
    this.updateEmployment(this.masterEmployeeService.changeProsalEffectiveDate(this.employee.id, this.employment.id, null));
  }

  onPracticeTypeChange($event) {
    this.updateEmployment(
      this.masterEmployeeService.changeProsalPracticeType(this.employee.id, this.employment.id, this.employment.prosalInfo.practiceType));
  }

  onServicePercentChange($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    if (inputElement.validity.valid) {
      this.servicePercentErrorMessage = '';
      this.updateEmployment(
        this.masterEmployeeService
          .changeProsalServicePercent(this.employee.id, this.employment.id, this.employment.prosalInfo.servicePercent));
    } else {
      this.servicePercentErrorMessage = 'Value entered is invalid. Please use single number or 0-100 format.';
    }
  }

  onServicePercentDynamicChange($event: Event) {
    this.employment.prosalInfo.servicePercent= this.employment.prosalInfo.servicePercentDynamic ? this.SERVICE_PERCENT_DYNAMIC_VALUE : '';
    this.updateEmployment(
      this.masterEmployeeService
        .toggleProsalServicePercentDynamic(this.employee.id, this.employment.id, this.employment.prosalInfo.servicePercentDynamic));
  }

  onProductPercentChange($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    if (inputElement.validity.valid) {
      this.productPercentErrorMessage = '';
      this.updateEmployment(
        this.masterEmployeeService
          .changeProsalProductPercent(this.employee.id, this.employment.id, this.employment.prosalInfo.productPercent));
    } else {
      this.productPercentErrorMessage = 'Value entered is invalid. Please use single number or 0-100 format.';
    }
  }

  onDiCommissionChange($event) {
    this.updateEmployment(
      this.masterEmployeeService
        .changeProsalDiCommission(this.employee.id, this.employment.id, this.employment.prosalInfo.diCommission));
  }

  onProsalFrequencyChange($event) {
    this.updateEmployment(
      this.masterEmployeeService
        .changeProsalFrequency(this.employee.id, this.employment.id, this.employment.prosalInfo.frequency));
  }

  onProsalEndDateChange($event) {
    const pipe = new DatePipe('en-US');
    const formattedDate = pipe.transform($event.value, 'yyyy-MM-dd','+0000' );
    this.updateEmployment(this.masterEmployeeService.changeProsalEndDate(this.employee.id, this.employment.id, formattedDate));
  }

  clearProsalEndDate() {
    this.prosalEndDate.setValue(null);
    this.updateEmployment(this.masterEmployeeService.changeProsalEndDate(this.employee.id, this.employment.id, null));
  }

  onServiceExclusionsChange($event: MatSelectChange) {
    this.masterEntities.aahaAccounts.subscribe(aahaAccounts => {
      this.updateEmployment(this.masterEmployeeService.updateAahaServiceAccountsExclusions(
        this.employee.id, this.employment.id, $event.value.map(
          aahaAccountId => aahaAccounts.filter(item => item.id === aahaAccountId)[0])));
    });
  }

  onProductExclusionsChange($event: MatSelectChange) {
    this.masterEntities.aahaAccounts.subscribe(aahaAccounts => {
      this.updateEmployment(this.masterEmployeeService.updateAahaProductAccountsExclusions(
        this.employee.id, this.employment.id, $event.value.map(
          aahaAccountId => aahaAccounts.filter(item => item.id === aahaAccountId)[0])));
    });
  }

  updateEmployment(operation) {
    this.spinnerService.showSpinner();
    operation
      .pipe(
        finalize(() => {
          console.log('Employment has been updated.');
          this.spinnerService.hideSpinner();
        }))
      .subscribe(response => {
          this.toastrService.info(`Employment has been updated.`);
        },
        response => {
          this.toastrService.error(
            `Error updating employment : \n${response.error.status} :  ${response.error.error}`)
        });
  };
  onProsalChange(employment: Employment) {
    this.masterEmployeeService.activateProsal(employment.employee.id, employment.id, employment.prosalInfo.active)
      .subscribe(response => {
        this.toastrService.info(`Prosal has been updated.`);
      });
  }
}

@Pipe({
  name: 'sortByName'
})
export class SortByNamePipe implements PipeTransform {
  transform(value: any[]): any[] {
    return value != null ? value.sort((a, b) => {
      let result = 0;
      if (a.name < b.name) {
        result = -1;
      } else if (a.name > b.name) {
        result = 1;
      }
      return result;
    }) : value;
  }
}

@Pipe({
  name: 'aahaAccountRevenueCategory'
})
export class AahaAccountRevenueCategoryPipe implements PipeTransform {
  transform(items: any[], revenueCategory: string): any[] {
    return items?.filter(item => item.revenueCategory === revenueCategory);
  }
}
